import * as Stardog from 'stardog';
import { MimeType } from 'vet-bones/constants';
import { formatJsonLd, getStardogConnection, isBody } from 'vet-bones/utils';

import { refreshConnectionToken } from 'src/actions/connections/action-creators/refreshConnectionToken';
import {
  API,
  APIReturn,
  AttemptResponse,
  FailureResponse,
  Options,
  RequestAction,
  SuccessResponse,
} from 'src/actions/request/types';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { Connection } from 'src/store/connections/types';
import { getStore } from 'src/store/store';
import * as copy from 'src/templates/copy';

const connectionRefusedChecker = /CONNECTION_REFUSED$/;

// The headers object cannot be cloned using the structured clone algorithm, so
// this converts the headers to a cloneable object.
export const getCloneableResponse = (response: Stardog.HTTP.Body) => {
  if (!response || !response.headers) {
    return response;
  }

  const cloneableHeaders: Record<string, string> = {};
  (response.headers as Headers).forEach((value, key) => {
    cloneableHeaders[key] = value;
  });

  return {
    ...response,
    headers: cloneableHeaders,
  };
};

export const request = async <
  Action extends RequestAction,
  APIMethod extends API,
  AttemptType extends string,
  FailureType extends string,
  SuccessType extends string
>(
  options: Options<Action, APIMethod, AttemptType, FailureType, SuccessType>
): Promise<
  | AttemptResponse<Action, AttemptType>
  | FailureResponse<Action, FailureType>
  | SuccessResponse<Action, APIMethod, SuccessType>
> => {
  const { api, args, action, config, shouldNotDispatch } = options;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { getState, dispatch } = getStore();
  const connection =
    (action.connection as Connection | undefined) ||
    getState().connections.connection;
  if (!connection) {
    throw new Error(copy.requests.noConnection);
  }

  const start = Date.now();
  if (!shouldNotDispatch && Boolean(options.attempt)) {
    const attemptAction: AttemptResponse<Action, AttemptType> = {
      type: options.attempt,
      payload: {
        args,
        action,
      },
    };
    dispatch(attemptAction as any);
  }

  let response;
  try {
    const conn = getStardogConnection(connection, {
      agent: 'Designer',
      ...config,
    });
    // @ts-ignore
    response = await api(conn, ...args);

    // retry with new connection token
    if (isBody(response) && !response.ok && response.status === 401) {
      // @ts-ignore
      const updatedConnection = await dispatch(refreshConnectionToken());
      if (updatedConnection) {
        // @ts-ignore
        const newConn = getStardogConnection(updatedConnection, {
          agent: 'Designer',
          ...config,
        });
        // @ts-ignore
        response = await api(newConn, ...args);
      }
    }

    if (isBody(response) && !response.ok) {
      throw new Error(response.statusText || String(response.status));
    }

    if (
      response.body &&
      response.headers &&
      response.headers.get('content-type') === MimeType.JSON_LD
    ) {
      response.body = await formatJsonLd(response.body);
    }

    const successAction: SuccessResponse<Action, APIMethod, SuccessType> = {
      type: options.success,
      payload: {
        args,
        action,
        response: getCloneableResponse(response) as APIReturn<APIMethod>,
        timeElapsed: Date.now() - start,
      },
    };

    if (!shouldNotDispatch) {
      dispatch(successAction as any);
    }
    return successAction;
  } catch (err) {
    const errorMessage =
      (err as Error).message || copy.requests.operationFailed;
    const failureAction: FailureResponse<Action, FailureType> = {
      type: options.failure,
      payload: {
        args,
        action,
        response: (getCloneableResponse(response) as any) || {
          id: args[0],
          errorMessage,
        },
        timeElapsed: Date.now() - start,
      },
    };

    if (!shouldNotDispatch) {
      dispatch(failureAction as any);
      if (connectionRefusedChecker.test((err as Error).message)) {
        // If connection is outright refused, we've lost (or never had) the
        // connection and need to set `isConnected` to `false` and bail.
        const connectionFailureAction: FailureResponse<
          Action,
          'REQUEST_VALIDATE_CONNECTION_FAILURE'
        > = {
          type: 'REQUEST_VALIDATE_CONNECTION_FAILURE', // literal here to avoid circular dependency
          payload: {
            args,
            action,
            response: {
              errorMessage: isConnectedSelector(getState())
                ? 'DISCONNECTED'
                : (err as Error).message,
            },
          },
        };
        dispatch(connectionFailureAction as any);
      } else if (response && response.status === 401) {
        // Handle connections that are unauthorized by forcing the user to
        // connect again. This should only happen when a access token has expired.
        const unauthorizedFailure: FailureResponse<Action, 'DISCONNECTED'> = {
          type: 'DISCONNECTED', // literal here to avoid circular dependency
          payload: {
            args,
            action,
            response,
          },
        };
        dispatch(unauthorizedFailure as any);
      }
    }
    return failureAction;
  }
};
